import React, { useEffect, useState, useCallback } from "react";
import { adminApi } from "../../utils/adminApi";
import { paths } from "../../paths";
import { getCache, setCache, isFresh, invalidate } from "./adminCache";
import {
  AdminShell,
  Panel,
  Modal,
  ResultModal,
  Chip,
  inputCls,
  labelCls,
  timeAgo,
  Loading,
} from "./adminUi";

const FILTERS = [
  { key: "submitted", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "", label: "All" },
];

const STATUS_CLS = {
  submitted: "bg-amber-100 text-amber-700",
  approved: "bg-teal-100 text-teal-700",
  rejected: "bg-red-100 text-red-600",
};

const isUrl = (s) => /^https?:\/\//i.test((s || "").trim());

// Checklist-type submissions store proof as a single generated string:
// "Checklist completed: ✓ item ✓ item ... Notes: extra text". Depending on
// how it round-trips through the API, the "\n" separators can get collapsed
// into plain spaces, which is what turns it into a wall of text. Parse it
// back into a real list + separate notes instead of relying on whitespace.
const CHECKLIST_PREFIX = /^checklist completed:\s*/i;

const parseChecklistProof = (text) => {
  if (!text || !CHECKLIST_PREFIX.test(text.trim())) return null;

  let body = text.trim().replace(CHECKLIST_PREFIX, "");
  let notes = "";

  const marker = body.lastIndexOf("Notes:");
  if (marker !== -1) {
    notes = body.slice(marker + "Notes:".length).trim();
    body = body.slice(0, marker).trim();
  }

  const items = body
    .split("✓")
    .map((s) => s.trim().replace(/^\d+\.\s*/, ""))
    .filter(Boolean);

  return items.length ? { items, notes } : null;
};

const ManageSubmissions = () => {
  const [filter, setFilter] = useState("submitted");
  const [submissions, setSubmissions] = useState(() => getCache(`submissions:${"submitted"}`) || []);
  const [loading, setLoading] = useState(() => !getCache(`submissions:${"submitted"}`));
  const [reviewing, setReviewing] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // popup after approve/reject

  const fetchSubmissions = useCallback(() => {
    const key = `submissions:${filter}`;
    adminApi
      .get("/submissions", { params: filter ? { status: filter } : {} })
      .then((res) => { setSubmissions(res.data || []); setCache(key, res.data || []); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => {
    const key = `submissions:${filter}`;
    const cached = getCache(key);
    if (cached) setSubmissions(cached);
    if (isFresh(key)) { setLoading(false); return; }
    setLoading(!cached);
    fetchSubmissions();
  }, [filter, fetchSubmissions]);

  const review = async (id, action) => {
    if (action === "reject" && !reason) {
      alert("Please add a rejection reason.");
      return;
    }
    const sub = reviewing;
    setBusy(true);
    try {
      await adminApi.patch(`/submissions/${id}/review`, {
        action,
        reviewNotes: reason || undefined,
      });
      setReviewing(null);
      setResult({ action, sub, reason });
      setReason("");
      invalidate(
        "submissions:submitted",
        "submissions:approved",
        "submissions:rejected",
        "submissions:",
        "cas",
        "leaderboard",
        "dashboard"
      );
      fetchSubmissions();
    } catch (err) {
      alert(err.response?.data?.message || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const openReview = (s) => {
    setReason(s.reviewNotes || "");
    setReviewing(s);
  };

  if (loading) return <Loading label="Loading submissions..." />;

  return (
    <AdminShell
      tab={paths.adminSubmissions}
      title="Submissions"
      subtitle="Review proof submitted by Campus Ambassadors and credit points."
    >
      <Panel
        title="Submissions"
        sub={`${submissions.length} ${filter ? FILTERS.find((f) => f.key === filter)?.label.toLowerCase() : "total"}`}
        action={
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <Chip key={f.key || "all"} active={filter === f.key} onClick={() => setFilter(f.key)}>{f.label}</Chip>
            ))}
          </div>
        }
      >
        {/* Fixed height so the panel keeps the same size whether the list is
            empty, short, or long (scrolls) — no layout jump between filters. */}
        <div className="overflow-x-auto h-[600px] overflow-y-auto">
          {submissions.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-black/40">
              No submissions found.
            </div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead className="sticky top-0 bg-[#F5EFEB] z-10">
                <tr className="text-[10px] font-extrabold tracking-wider text-[#2B1B17]/40 uppercase">
                  <th className="py-2 px-3">CA</th>
                  <th className="py-2 px-3">Task</th>
                  <th className="py-2 px-3 text-right">Pts</th>
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3">Submitted</th>
                  <th className="py-2 px-3"></th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {submissions.map((s) => (
                  <tr key={s.id} className="border-b border-black/[0.03] last:border-none">
                    <td className="py-2.5 px-3 whitespace-nowrap">
                      <p className="font-bold text-[#2B1B17]">{s.User?.name}</p>
                      <p className="text-[11px] text-black/40">{s.User?.college}</p>
                    </td>
                    <td className="py-2.5 px-3 text-black/60 whitespace-nowrap">{s.Task?.header}</td>
                    <td className="py-2.5 px-3 text-right font-bold text-green-600">+{s.Task?.points ?? 0}</td>
                    <td className="py-2.5 px-3">
                      <span className={`text-[9px] font-extrabold tracking-wider px-2 py-1 rounded-md capitalize ${STATUS_CLS[s.status] || "bg-gray-200 text-gray-500"}`}>{s.status}</span>
                    </td>
                    <td className="py-2.5 px-3 text-black/50 whitespace-nowrap">{timeAgo(s.submittedAt)}</td>
                    <td className="py-2.5 px-3 text-right">
                      <button onClick={() => openReview(s)} className="text-xs font-bold text-[#D15B28] hover:opacity-80 whitespace-nowrap">Review →</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Panel>

      {reviewing && (
        <Modal label="Review Submission" title={reviewing.Task?.header} onClose={() => setReviewing(null)} wide>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Proof */}
            <div>
              <p className={labelCls}>Submitted Link</p>
              {reviewing.proofLink ? (
                <a
                  href={isUrl(reviewing.proofLink) ? reviewing.proofLink.trim() : `https://${reviewing.proofLink.trim()}`}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1.5 flex items-center justify-between gap-2 bg-white border border-black/5 rounded-lg px-3 py-2.5 hover:border-[#D15B28] transition"
                >
                  <span className="text-sm text-[#D15B28] font-semibold truncate break-all">🔗 {reviewing.proofLink.trim()}</span>
                  <span className="text-xs text-black/40 flex-shrink-0">Open →</span>
                </a>
              ) : (
                <p className="text-sm text-black/40 mt-1.5">No link submitted.</p>
              )}

              {reviewing.proofText && (() => {
                const checklist = parseChecklistProof(reviewing.proofText);

                if (checklist) {
                  return (
                    <div className="mt-4">
                      <p className={labelCls}>Checklist Completed</p>
                      <div className="bg-white border border-black/5 rounded-lg divide-y divide-black/5 mt-1.5">
                        {checklist.items.map((item, i) => (
                          <div key={i} className="flex items-start gap-2.5 px-3 py-2">
                            <span className="text-teal-600 font-bold flex-shrink-0 mt-px">✓</span>
                            <span className="text-sm text-black/70 break-words">{item}</span>
                          </div>
                        ))}
                      </div>
                      {checklist.notes && (
                        <div className="mt-3">
                          <p className={labelCls}>Additional Notes</p>
                          <div className="bg-white border border-black/5 rounded-lg px-3 py-2.5 mt-1.5">
                            <p className="text-sm text-black/70 whitespace-pre-line break-words">{checklist.notes}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <div className="mt-4">
                    <p className={labelCls}>Notes from CA</p>
                    <div className="bg-white border border-black/5 rounded-lg px-3 py-2.5 mt-1.5">
                      <p className="text-sm text-black/70 whitespace-pre-line break-words">{reviewing.proofText}</p>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* CA + action */}
            <div className="flex flex-col gap-4">
              <div className="bg-[#2B1B17] rounded-xl p-4 text-white">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#D15B28] flex items-center justify-center font-bold text-lg flex-shrink-0">
                    {reviewing.User?.name?.charAt(0).toUpperCase() || "U"}
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-bold leading-tight truncate">{reviewing.User?.name}</h3>
                    <p className="text-xs text-white/60 truncate">{reviewing.User?.college || "—"}</p>
                  </div>
                </div>
              </div>

              <div>
                <p className={labelCls}>Task</p>
                <div className="bg-white border border-black/5 rounded-lg p-3 mt-1.5">
                  <p className="font-bold text-[#2B1B17]">{reviewing.Task?.header}</p>
                  <p className="text-[11px] text-black/40 mt-0.5">+{reviewing.Task?.points ?? 0} pts · submitted {timeAgo(reviewing.submittedAt)}</p>
                </div>
              </div>

              <div>
                <p className={labelCls}>Rejection Reason <span className="text-black/30 normal-case font-medium">(fill only if rejecting)</span></p>
                <input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Missing hashtag, wrong account used..."
                  className={`${inputCls} mt-1.5`}
                />
              </div>

              {reviewing.status === "submitted" ? (
                <div className="flex items-center gap-3 mt-1">
                  <button onClick={() => review(reviewing.id, "approve")} disabled={busy} className="flex-1 bg-teal-500 text-white font-semibold py-3.5 rounded-full hover:bg-teal-600 transition disabled:opacity-60">
                    {busy ? "..." : "✓ Approve & Credit"}
                  </button>
                  <button onClick={() => review(reviewing.id, "reject")} disabled={busy} className="px-6 py-3.5 rounded-full border border-red-300 text-red-500 font-semibold hover:bg-red-50 transition disabled:opacity-60">
                    ✕ Reject
                  </button>
                </div>
              ) : (
                <p className="text-sm text-black/50 mt-1 capitalize">
                  This submission is already <strong>{reviewing.status}</strong>.
                  {reviewing.reviewNotes ? ` Note: "${reviewing.reviewNotes}"` : ""}
                </p>
              )}
            </div>
          </div>
        </Modal>
      )}

      {result?.action === "approve" && (
        <ResultModal
          accent="teal"
          title="Submission Approved!"
          message={`+${result.sub?.Task?.points ?? 0} pts credited to ${result.sub?.User?.name || "this CA"}. They have been notified automatically.`}
          chip={`+${result.sub?.Task?.points ?? 0} pts · ${result.sub?.Task?.header || "Task"}`}
          primary={{ label: "Next Submission", onClick: () => setResult(null) }}
          secondary={{ label: "Done", onClick: () => setResult(null) }}
          onClose={() => setResult(null)}
        />
      )}

      {result?.action === "reject" && (
        <ResultModal
          accent="red"
          danger
          title="Submission Rejected"
          message={`${result.sub?.User?.name || "The CA"} has been notified. No points were credited.`}
          chip={result.reason ? `Reason · ${result.reason}` : undefined}
          primary={{ label: "Next Submission", onClick: () => setResult(null) }}
          secondary={{ label: "Done", onClick: () => setResult(null) }}
          onClose={() => setResult(null)}
        />
      )}
    </AdminShell>
  );
};

export default ManageSubmissions;